import { useEffect, useRef, useState, MouseEvent } from "react";
import Icon, { IconWithTooltip } from "components/Elements/Icon/Icon";
import { useContent } from "contexts/contentContext/contentContext";
import scrollHorizontally from "./helpers/scrollHorizontally";
import type { QueryParamDropdownListT } from "../QueryParamDropdownFilter/helpers/useQueryParamDropdown";
import getSelectedCount from "components/Pages/AccountUsers/helpers/getSelectedCount";
import useMediaQueries from "hooks/useMediaQueries";
import { useAppSelector } from "store/hooks";
import { selectSearchParams } from "store/selectors/inboxSelectors";
import { setSearchParams } from "../../../store/slices/inboxSlice";

import "./styles.scss";

type QueryParamsBarPropsT = {
  params: QueryParamDropdownListT;
  onChange: (value: QueryParamDropdownListT) => void;
};

const QueryParamsBar = ({ params, onChange }: QueryParamsBarPropsT) => {
  const searchParams = useAppSelector(selectSearchParams);
  const ulRef = useRef<HTMLUListElement>(null);
  const [showArrows, setShowArrows] = useState(false);
  const { getTextByKey, getDirection } = useContent();
  const { mobileCondition } = useMediaQueries();

  useEffect(() => {
    const res: QueryParamDropdownListT = {};
    for (const name in params) {
      const queryParams = searchParams[name as keyof typeof searchParams];

      if (queryParams) {
        res[name as keyof QueryParamDropdownListT] = Array.isArray(queryParams)
          ? queryParams
          : [queryParams];
      }
    }

    onChange(res);
  }, []);

  useEffect(() => {
    if (ulRef.current) {
      setShowArrows(
        Math.floor(document.body?.clientWidth * 0.2) <=
          ulRef.current?.clientWidth
      );
    }
  }, [ulRef.current?.clientWidth]);

  const handleScroll = (
    _: MouseEvent<HTMLDivElement>,
    direction: "left" | "right"
  ) => {
    const length = Object.values(params).flat()?.length;
    scrollHorizontally(ulRef.current, direction, length);
  };

  const handleRemove = (index: number, param: string, category: string) => {
    ulRef.current?.children[index].classList.add("li-exit");
    let res: { [key: string]: string[] } = {};
    let resStrings: { [key: string]: string } = {};

    for (const name in params) {
      res[name as keyof QueryParamDropdownListT] = params[name]
        ?.filter((p): p is string => typeof p === "string")
        .filter((p) => p !== param && category === name);
      resStrings[name as keyof QueryParamDropdownListT] = params[name]
        ?.filter((p): p is string => typeof p === "string")
        .filter((p) => p !== param && category === name)[0];
    }

    setSearchParams(resStrings);
    onChange?.(res);
  };

  const clearAll = () => {
    const payload: { [key: string]: [] } = {};
    const payloadWithArrays: QueryParamDropdownListT = {};

    Object.keys(params).forEach((paramName) => {
      payload[paramName] = [];
      payloadWithArrays[paramName] = [];
    });
    setSearchParams(payload);
    onChange?.(payloadWithArrays);
  };

  return (
    !mobileCondition && (
      <div className="status-filter-bar-wrapper status-query-filter-bar-wrapper">
        {showArrows && (
          <div
            className="arrow-icon-wrapper"
            onClick={(e: any) => handleScroll(e, "left")}
          >
            <Icon
              name={getDirection() === "ltr" ? "arrow-left" : "arrow-right"}
              gray
              small
              alt="scroll-left"
            />
          </div>
        )}
        <ul ref={ulRef}>
          {Object.entries(params).map(([name, values]) => {
            return (
              values?.map((param, i) => (
                <li key={param as string}>
                  {getTextByKey(param)}
                  <IconWithTooltip
                    iconProps={{
                      name: "x",
                      onClick: () => handleRemove(i, param as string, name),
                      gray: true,
                      iconClass: "remove-icon",
                      alt: "remove",
                    }}
                    tooltipProps={{
                      tooltipTextKey: "remove-filter-status",
                    }}
                  />
                </li>
              )) || []
            );
          })}
        </ul>
        {showArrows && (
          <div
            className="arrow-icon-wrapper"
            onClick={(e: any) => handleScroll(e, "right")}
          >
            <Icon
              name={getDirection() === "ltr" ? "arrow-right" : "arrow-left"}
              gray
              small
              alt="scroll-right"
            />
          </div>
        )}
        {!!getSelectedCount(params) && (
          <button onClick={clearAll}>{getTextByKey("filter-clear-all")}</button>
        )}
      </div>
    )
  );
};

export default QueryParamsBar;
